// Consignment and outsourced production mutations, spread into `mutations` (shared/mutations.ts).
// Implementations: server/src/mutations/consignment.ts (and make-output.ts for output reports, which receiving
// also uses). Shared rules: shared/consignment.ts.
import { z } from "zod";
import { COUNT_POLICIES, MAKE_STAGES, SCRAP_REASONS } from "./consignment.ts";
import { ADMIN, custom, STAFF } from "./mutation-kit.ts";
import { planBasisSchema } from "./po.ts";

const RECEIVERS = ["admin", "inventory", "receiving"] as const;

const text = (max = 200) => z.string().trim().max(max);
const units = z.number().positive("Quantities are more than 0").max(10_000_000);
const isoDate = z.iso.date("Use a date like 2026-12-31").nullable();

export const cmFields = z.object({ is_cm: z.boolean(), count_policy: z.enum(COUNT_POLICIES) });
export const makeOrderFields = z.object({ stage: z.enum(MAKE_STAGES), notes: text(5000) });

const scrapLine = z.object({
  item_id: z.uuid(),
  qty: units,
  reason: z.enum(SCRAP_REASONS),
  notes: text(1000).optional(),
});

/** A build on a make PO: an item, how many, and (optionally) the BOM revision and the CM's price per unit. */
export const makeLineInput = z.object({
  /** The make order's id; its PO line gets one of its own. */
  id: z.uuid(),
  /** Make planning --- the PO line's id, made by the browser so the new line shows (and counts in the plan) at once. */
  line_id: z.uuid().optional(),
  item_id: z.uuid(),
  qty: units,
  bom_id: z.uuid().optional(),
  unit_price: z.number().nonnegative("Price must be 0 or more").nullable().optional(),
  /** Check against the plan --- what the plan said when it was drafted from it (po_line.plan_basis). */
  plan_basis: planBasisSchema.optional(),
});

export const consignmentMutations = {
  /**
   * Makes a location a vendor's CM site: an ignored Shopify location (e.g. "Tailhoo Technology", whose Shopify
   * stock is then pulled) or one of the vendor's sites. With `name` instead of `id`, a new site (not in Shopify).
   */
  "location.make_cm_site": custom(
    z
      .object({
        id: z.uuid().optional(),
        name: text().optional(),
        new_id: z.uuid().optional(),
        vendor_id: z.uuid(),
        count_policy: z.enum(COUNT_POLICIES).optional(),
      })
      .refine((a) => a.id || (a.name && a.new_id), "Pick a location or name a new site"),
    ADMIN,
  ),
  /** Count policy (staff); is_cm on or off (admins, and never off while the CM holds stock or open orders). */
  "location.update_cm": {
    kind: "update" as const,
    table: "location" as const,
    roles: STAFF,
    args: z.object({ id: z.uuid(), patch: cmFields.partial() }),
  },

  /**
   * Ships parts HHL → CM (consign_ship moves) or CM → HHL (return moves), posted at once: they leave one bin
   * and land in the other side's default bin, in transit until the other side confirms. Local items only.
   */
  "consign.ship": custom(
    z.object({
      id: z.uuid(),
      direction: z.enum(["to_cm", "to_hhl"]).default("to_cm"),
      cm_location_id: z.uuid(),
      /** HHL's side; HHL's (first) warehouse if left out. */
      hhl_location_id: z.uuid().optional(),
      carrier: text(100).optional(),
      tracking: text(200).optional(),
      eta: isoDate.optional(),
      notes: text(5000).optional(),
      lines: z
        .array(z.object({ item_id: z.uuid(), qty: units, notes: text(1000).optional(), bin_id: z.uuid().optional() }))
        .min(1, "Add something to ship")
        .max(500),
    }),
    STAFF,
  ),
  /**
   * The other side has it (HHL confirms for the CM until the portal): each line's received quantity, all of it
   * if left out. What didn't arrive is written off as lost (a scrap move where it should have landed).
   */
  "consign.receive": custom(
    z.object({
      id: z.uuid(),
      lines: z
        .array(z.object({ line_id: z.uuid(), qty_received: z.number().nonnegative().max(10_000_000) }))
        .max(500)
        .optional(),
      notes: text(5000).optional(),
    }),
    RECEIVERS,
  ),

  /**
   * A make PO to a CM vendor: each line builds an item from its BOM (the current revision unless bom_id) at the
   * vendor's CM site, its price the vendor's link price, else what the BOM says the CM charges. Ships to HHL
   * unless ship_to_location_id says otherwise (the CM itself, for sub-assemblies it keeps for the next build).
   */
  "po.create_make": custom(
    z.object({
      id: z.uuid(),
      vendor_id: z.uuid(),
      cm_location_id: z.uuid().optional(),
      ship_to_location_id: z.uuid().nullable().optional(),
      expected_ship: isoDate.optional(),
      expected_arrive: isoDate.optional(),
      notes: text(5000).optional(),
      /** Production runs --- the run this make PO builds (it's linked to it). */
      production_run_id: z.uuid().optional(),
      lines: z.array(makeLineInput).max(200).default([]),
    }),
    STAFF,
  ),
  /** Adds a build to a make PO (a new line), or gives an existing line of one its BOM snapshot (po_line_id). */
  "make_order.create": custom(
    z
      .object({
        id: z.uuid(),
        po_id: z.uuid(),
        po_line_id: z.uuid().optional(),
        item_id: z.uuid().optional(),
        qty: units.optional(),
        bom_id: z.uuid().optional(),
        unit_price: z.number().nonnegative().nullable().optional(),
      })
      .refine((a) => a.po_line_id || (a.item_id && a.qty), "Pick a PO line, or an item and a quantity"),
    STAFF,
  ),
  /** Takes a new snapshot of the BOM (its current revision, or bom_id) while nothing has been reported yet. */
  "make_order.refresh_bom": custom(z.object({ id: z.uuid(), bom_id: z.uuid().optional() }), STAFF),
  "make_order.update": {
    kind: "update" as const,
    table: "make_order" as const,
    roles: STAFF,
    args: z.object({ id: z.uuid(), patch: makeOrderFields.partial() }),
  },
  /**
   * The CM built `qty` more: their consigned components are consumed at the CM (per unit with scrap allowance ×
   * output, less what earlier reports consumed), the finished goods recorded there (local items: a make_output
   * move; Shopify items: on the order, until they're received through receiving). Plus any extra scrap.
   */
  "make_order.report_output": custom(
    z.object({
      id: z.uuid(),
      make_order_id: z.uuid(),
      qty: units,
      scrap: z.array(scrapLine).max(200).optional(),
      notes: text(5000).optional(),
      /** Confirms output above what was ordered. */
      allow_over: z.boolean().optional(),
    }),
    STAFF,
  ),
  /** Scrap or loss at a CM, with a reason (optionally against a make order). */
  "cm.report_scrap": custom(
    z.object({
      id: z.uuid(),
      location_id: z.uuid(),
      make_order_id: z.uuid().optional(),
      lines: z.array(scrapLine).min(1, "Add what was scrapped").max(200),
      notes: text(5000).optional(),
    }),
    STAFF,
  ),

  /**
   * A count at a CM: each line's book quantity as of counted_at and the variance. `post` (Mitch, 2026-11-03: "it's a bit
   * annoying that I have to approve it. I already went through the process"): posted as it's entered, the timing
   * questions answered in `decisions` by whoever counted; with one unanswered it's saved, not posted, and the questions
   * come back ({ posted: false, questions }) for the same dialog to ask, then cm_count.approve posts it.
   */
  "cm_count.submit": custom(
    z.object({
      id: z.uuid(),
      location_id: z.uuid(),
      /** When the CM counted; now if left out. */
      counted_at: z.iso.datetime({ offset: true }).optional(),
      make_order_id: z.uuid().optional(),
      notes: text(5000).optional(),
      lines: z
        .array(z.object({ item_id: z.uuid(), counted: z.number().nonnegative("Counts are 0 or more").max(10_000_000) }))
        .min(1, "Enter at least one count")
        .max(2000),
      post: z.boolean().optional(),
      decisions: z
        .record(z.string().regex(/^(consign_shipment|cm_report|receipt|move):[0-9a-f-]{36}$/, "Not something a count asks about"), z.enum(["before", "after"]))
        .refine((d) => Object.keys(d).length <= 1000, "Too many answers")
        .optional(),
    }),
    STAFF,
  ),
  /**
   * Posts each local part's variance still unexplained as a count move at the CM. Anything recorded after the count
   * that may or may not be in it (reviewCount() in shared/consignment.ts lists them) must be placed: key → before/after.
   */
  "cm_count.approve": custom(
    z.object({
      id: z.uuid(),
      notes: text(5000).optional(),
      decisions: z
        .record(z.string().regex(/^(consign_shipment|cm_report|receipt|move):[0-9a-f-]{36}$/, "Not something a count asks about"), z.enum(["before", "after"]))
        .refine((d) => Object.keys(d).length <= 1000, "Too many answers")
        .optional(),
    }),
    STAFF,
  ),
  "cm_count.reject": custom(z.object({ id: z.uuid(), notes: text(5000).min(1, "Say why the count is rejected") }), STAFF),
} as const;
